import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
} from '@mui/material'
import {
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  BugReport as BugReportIcon,
  Menu as MenuIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@store/hooks'
import { logout } from '@store/slices/authSlice'
import { useLogoutMutation } from '@store/api/authApi'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import NotificationBell from '@components/notifications/NotificationBell'
import OfflineIndicator from '@components/shared/OfflineIndicator'
import { APP_SHELL_NAV_DRAWER_ID } from './layoutConstants'

const MAX_MENU_NAME_LENGTH = 24

function truncateDisplayName(name: string): string {
  if (name.length <= MAX_MENU_NAME_LENGTH) return name
  return `${name.slice(0, MAX_MENU_NAME_LENGTH - 1)}…`
}

function userDisplayName(user: { firstName?: string | null; username?: string | null } | null): string {
  return user?.firstName || user?.username || 'User'
}

type HeaderProps = {
  showMenuButton?: boolean
  onMenuToggle?: () => void
  menuOpen?: boolean
}

const Header: React.FC<HeaderProps> = ({
  showMenuButton = false,
  onMenuToggle,
  menuOpen = false,
}) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [logoutMutation] = useLogoutMutation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const fullDisplayName = userDisplayName(user)
  const displayName = truncateDisplayName(fullDisplayName)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap()
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      dispatch(logout())
      navigate('/auth/login')
      handleMenuClose()
    }
  }

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderBottom: '1px solid',
          borderBottomColor: 'primary.dark',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1 }}>
          {showMenuButton && (
            <IconButton
              color="inherit"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              aria-controls={APP_SHELL_NAV_DRAWER_ID}
              edge="start"
              onClick={onMenuToggle}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            component="div"
            onClick={() => navigate(isAuthenticated ? '/app' : '/')}
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontFamily: 'Playfair Display, serif',
              fontWeight: 600,
              letterSpacing: '-0.025em',
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            CigarTrack
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {isAuthenticated && <OfflineIndicator />}
            {isAuthenticated && <NotificationBell />}

            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              aria-label={`Account menu for ${fullDisplayName}`}
              aria-haspopup="true"
              aria-controls="header-account-menu"
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                <AccountCircleIcon fontSize="small" />
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        id="header-account-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            mt: 1,
            minWidth: 200,
            maxWidth: 'min(280px, 100vw - 16px)',
          },
        }}
      >
        {isAuthenticated && (
          <MenuItem disabled sx={{ opacity: 1, color: 'text.primary' }}>
            <Typography
              variant="body2"
              noWrap
              title={fullDisplayName}
              sx={{ fontWeight: 600, maxWidth: 240 }}
            >
              {displayName}
            </Typography>
          </MenuItem>
        )}
        {isAuthenticated && <Divider />}
        <MenuItem onClick={() => { navigate('/app/feedback'); handleMenuClose() }}>
          <BugReportIcon sx={{ mr: 1 }} />
          Report Issue
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <LogoutIcon sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>
    </>
  )
}

export default Header
